import { Collapsible } from "@simplicity/ui";
import { ScheduleAuditContext } from "../schedules/ScheduleAuditContext";
import type { ScheduleDef } from "../schedules/scheduleRows";
import { SchedulesPanel } from "../schedules/SchedulesPanel";
import type { TableConfig } from "../tables/types";
import { DeansListForm } from "./DeansListForm";
import { DnAForm } from "./DnAForm";
import { FileDropForm } from "./FileDropForm";
import type { ConnectorFormProps } from "./GenericVarsForm";
import { GenericVarsForm } from "./GenericVarsForm";
import { IReadySftpForm } from "./IReadySftpForm";
import { PowerSchoolsForm } from "./PowerSchoolsForm";
import { PSEnrollmentForm } from "./PSEnrollmentForm";
import { readPSScoping } from "./psScoping";
import { PSScopingContext } from "./PSScopingContext";
import { SchoolMintForm } from "./SchoolMintForm";
import { SftpServerForm } from "./SftpServerForm";
import { SkywardForm } from "./SkywardForm";

/** Keys each typed form reads from vars (excluding tables/schedules/baseUrl). */
const KNOWN_KEYS: Record<string, Set<string>> = {
  powerschools: new Set(["bronzeDataset", "silverDataset", "schoolid", "filters"]),
  "powerschool-enrollment": new Set(["bronzeDataset", "silverDataset"]),
  skyward: new Set(["entityId", "entityIds", "bronzeDataset", "silverDataset"]),
  deanslist: new Set([
    "domain",
    "lookbackDays",
    "maxDaysPerRequest",
    "bronzeDataset",
    "silverDataset",
  ]),
  schoolmint: new Set([
    "domain",
    "accountEmail",
    "siteName",
    "sftpUsername",
    "gcsBucketOverride",
    "gcsPrefixOverride",
    "pollIntervalMs",
    "pollMaxAttempts",
    "bronzeDataset",
    "silverDataset",
  ]),
  "schoolmint-enroll": new Set([
    "siteName",
    "sftpUsername",
    "gcsBucketOverride",
    "gcsPrefixOverride",
    "bronzeDataset",
    "silverDataset",
  ]),
  avela: new Set([
    "siteName",
    "sftpUsername",
    "gcsBucketOverride",
    "gcsPrefixOverride",
    "bronzeDataset",
    "silverDataset",
  ]),
  "iready-sftp": new Set([
    "projectId",
    "region",
    "host",
    "port",
    "username",
    "sftpWrapperUrl",
    "bronzeDataset",
    "silverDataset",
  ]),
  "file-drop": new Set(["acquisition", "bronzeDataset", "silverDataset"]),
  dna: new Set(["oauthUrl", "pageSize", "bronzeDataset", "silverDataset"]),
};

const RESERVED_KEYS = new Set(["tables", "schedules", "scheduleDefs", "baseUrl"]);

/** A vars key as an object, or empty — `vars` is untyped JSON and any key may be absent. */
function asRecord<T>(value: unknown): Record<string, T> {
  return value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, T>)
    : {};
}

/** The entries in `vars.tables` that carry a usable name — it is untyped JSON. */
function tablesOf(value: unknown): TableConfig[] {
  if (!Array.isArray(value)) return [];
  return value.filter((t): t is TableConfig => {
    const name = (t as { name?: unknown } | null)?.name;
    return typeof name === "string" && name !== "";
  });
}

function renderForm(props: ConnectorFormProps) {
  switch (props.connectorType) {
    case "powerschools":
      return <PowerSchoolsForm {...props} />;
    case "powerschool-enrollment":
      return <PSEnrollmentForm {...props} />;
    case "skyward":
      return <SkywardForm {...props} />;
    case "deanslist":
      return <DeansListForm {...props} />;
    case "schoolmint":
      return <SchoolMintForm {...props} />;
    case "schoolmint-enroll":
    case "avela":
      return <SftpServerForm {...props} />;
    case "iready-sftp":
      return <IReadySftpForm {...props} />;
    case "file-drop":
      return <FileDropForm {...props} />;
    case "dna":
      return <DnAForm {...props} />;
    default:
      return <GenericVarsForm {...props} />;
  }
}

function UnrecognizedVars({ entries }: { entries: [string, unknown][] }) {
  return (
    <div className="mt-2 rounded border border-border/50 text-sm">
      <Collapsible
        trigger={`Unrecognized variables (${entries.length})`}
        triggerClassName="px-3 py-2"
      >
        <div className="border-t border-border/50 px-3 py-2">
          <pre className="whitespace-pre-wrap font-mono text-xs text-muted-foreground">
            {JSON.stringify(Object.fromEntries(entries), null, 2)}
          </pre>
        </div>
      </Collapsible>
    </div>
  );
}

interface ConnectorVarsFormProps extends ConnectorFormProps {
  /** Relayed from the Schedules panel to whatever owns Save — see `SchedulesPanelProps`. */
  onScheduleInvalidChange?: (reason: string | null) => void;
}

export function ConnectorVarsForm({ onScheduleInvalidChange, ...props }: ConnectorVarsFormProps) {
  const knownKeys = KNOWN_KEYS[props.connectorType];
  const scheduleDefs = asRecord<ScheduleDef>(props.vars.scheduleDefs);
  const tables = tablesOf(props.vars.tables);

  // Compute unrecognized keys (skip for generic/unknown types)
  let unrecognizedEntries: [string, unknown][] = [];
  if (knownKeys) {
    unrecognizedEntries = Object.entries(props.vars).filter(
      ([k]) => !RESERVED_KEYS.has(k) && !knownKeys.has(k),
    );
  }

  return (
    // Project scoping is provided here, not inside the typed form, so the schedules panel
    // is inside it rather than a sibling of it — context flows down, not sideways (SIM-3402).
    <PSScopingContext.Provider value={readPSScoping(props.vars, props.connectorType)}>
      <ScheduleAuditContext.Provider
        value={{ connectorType: props.connectorType, vars: props.vars }}
      >
        <SchedulesPanel
          scheduleDefs={scheduleDefs}
          schedules={asRecord(props.vars.schedules)}
          tables={tables}
          onScheduleDefsChange={(next) => props.onVarsChange({ ...props.vars, scheduleDefs: next })}
          onSchedulesChange={(next) => props.onVarsChange({ ...props.vars, schedules: next })}
          clearBanner={props.clearBanner}
          onInvalidChange={onScheduleInvalidChange}
        />
      </ScheduleAuditContext.Provider>
      {renderForm(props)}
      {unrecognizedEntries.length > 0 && <UnrecognizedVars entries={unrecognizedEntries} />}
    </PSScopingContext.Provider>
  );
}
